import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import PermissionSet from "../../interfaces/permissionSet";
import SearchResultGrid from "../searchResultGrid/searchResultGrid";
import permissionSetColumnDef from "../../colDef/permissionSetColDef";
import apiClient from "../../axiosClient";
import HandlePermissionSetModal from "../modals/handlePermissionSetModal";
import { GridRowSelectionModel } from "@mui/x-data-grid";
import UploadComponent from "../baseComponents/uploadComponent";
import { permissionSetUploadFormat } from "../../constants/columnFormats";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { buildQueryObjectFromRecord } from "../../utils/commonUtils";
import Filter from "../../interfaces/filters";
import FilterDrawer from "../dashboard/filterDrawer";
import { getSelectedFiltersCount } from "../../utils/filterUtils";

const modalModes = { create: 'create', edit: 'edit' };

const ManagePermissionSet: React.FC = () => {
    const permissions = useSelector((state: RootState) => state.permissions.permissions).filter(val => val.is_active).map(val => val.permission_name);
    const users = useSelector((state: RootState) => state.userList.users).map(val => val.username);
    const [permissionSet, setPermissionSet] = useState<PermissionSet[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [addUserPermissionModalOpen, setAddUserPermissionModalOpen] = useState<boolean>(false);
    const [permissionUploadModalOpen, setPermissionUploadModalOpen] = useState<boolean>(false);
    const [leftMargin, setLeftMargin] = useState<string>("16px");
    const [drawerVisible, setDrawerVisible] = useState<boolean>(false);
    const [filters, setFilters] = useState<Filter[]>([
        { id: "username", key: "username", type: "dropdown", label: "Select User", value: [], list: users, disabled: false, multiple: true, filterOptions: false },
        { id: "permission_name", key: "permission_name", type: "dropdown", label: "Select Permission Name", value: [], list: permissions, disabled: false, multiple: true, filterOptions: false },
        { id: "is_active", key: "is_active", type: "dropdown", label: "Is Permission Active", list: ["true", "false"], value: "", disabled: false, multiple: false, filterOptions: false },
        { id: "license_count_le", key: "license_count_le", type: "number", label: "License Count Less Than", value: "", disabled: false, filterOptions: false, multiple: false },
        { id: "license_count_ge", key: "license_count_ge", type: "number", label: "License Count Greater Than", value: "", disabled: false, filterOptions: false, multiple: false },
    ]);
    const [selectedPermissions, setSelectedPermissions] = useState<PermissionSet[]>([]);
    const [modalMode, setModalMode] = useState<string>(modalModes.create);

    const openModal = (mode: string) => {
        setModalMode(mode);
        setAddUserPermissionModalOpen(true);
    };

    const toggleDrawer = () => {
        setDrawerVisible(!drawerVisible);
        if (drawerVisible) {
            setLeftMargin("16px");
        } else {
            setLeftMargin("166px");
        }
    };

    const getAllPermissionSet = useCallback(async () => {
        try {
            setLoading(true);
            const filterQuery = buildQueryObjectFromRecord(filters);
            const response = await apiClient.get('/api/v1/permission_set?' + filterQuery);
            if (response.status === 200) {
                setPermissionSet(response.data);
            }
        } catch (error: any) {
            if (error.response) {
                alert('Error response data:' + error.response.data.message);
            } else if (error.request) {
                alert('No response received:' + error.request);
            } else {
                alert('Error message:' + error.message);
            }
        } finally {
            setSelectedPermissions([]);
            setLoading(false);
        }
    }, [filters]);

    const handleSelectedRows = (ids: GridRowSelectionModel) => {
        setSelectedPermissions(permissionSet.filter(val => ids.ids.has(val.permission_id)));
    };

    const resetFilters = () => {
        setFilters(filters.map(val => {
            if (val.multiple) {
                val.value = [];
            } else {
                val.value = "";
            }
            return val;
        }));
    };

    useEffect(() => {
        getAllPermissionSet();
    }, [filters]);

    return (
        <Box sx={{ ml: leftMargin, mt: 2, mb: 2, mr: 2 }}>
            <FilterDrawer
                drawerOpen={drawerVisible}
                toggleDrawer={toggleDrawer}
                filters={filters}
                setFilters={setFilters}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Button
                        variant={getSelectedFiltersCount(filters) === 0 ? "outlined" : "contained"}
                        onClick={toggleDrawer}
                    >
                        <Typography>Filters [{getSelectedFiltersCount(filters)}]</Typography>
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={resetFilters}
                        sx={{ ml: 2 }}
                    >
                        <Typography>Reset</Typography>
                    </Button>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>
                    {selectedPermissions.length === 1 ? (
                        <Button sx={{ mr: 2 }} variant="outlined" onClick={() => openModal(modalModes.edit)}>
                            <Typography>Edit User Permission</Typography>
                        </Button>
                    ) : null}
                    <Button sx={{ mr: 2 }} variant="outlined" onClick={() => setPermissionUploadModalOpen(true)}>
                        <Typography>Upload User Permissions</Typography>
                    </Button>
                    <Button variant="outlined" onClick={() => openModal(modalModes.create)}>
                        <Typography>Add User Permission</Typography>
                    </Button>
                </Box>
            </Box>
            {loading ? (
                <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <CircularProgress color="primary" />
                </Box>
            ) : (
                <SearchResultGrid
                    columns={permissionSetColumnDef}
                    results={permissionSet}
                    rowCount={permissionSet.length}
                    onSelectRow={handleSelectedRows}
                    getRowId={(row: PermissionSet) => row.permission_id}                    
                />
            )}
            {addUserPermissionModalOpen && (
                <HandlePermissionSetModal
                    open={addUserPermissionModalOpen}
                    onClose={() => setAddUserPermissionModalOpen(false)}
                    getAPIFunction={getAllPermissionSet}
                    modalMode={modalMode}
                    selectedPermissionSet={selectedPermissions.length > 0 ? selectedPermissions[0] : undefined}
                />
            )}
            <UploadComponent
                open={permissionUploadModalOpen}
                onClose={() => setPermissionUploadModalOpen(false)}
                label="Upload Permissions for Users"
                uploadApi="/api/v1/upload_permission_set"
                uploadType="Permission File"
                uploadFormat={permissionSetUploadFormat}
            />
        </Box>
    );
};

export default ManagePermissionSet;